import type { HealthStatus, ReadinessPayload, WireService } from '@shared/contract';

/** Compact elapsed time: 47s, 12m, 3h 20m, 2d 4h. */
export function duration(from: string | Date | null | undefined): string {
  if (!from) return '—';
  const ms = Date.now() - new Date(from).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export function latency(ms: number | null | undefined): string {
  if (ms == null) return '—';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/** More precision only where it carries meaning — 99.99% vs 97.4%. */
export function uptime(pct: number | null): string {
  if (pct == null) return '—';
  return `${pct.toFixed(pct >= 99.95 ? 2 : 1)}%`;
}

/** Status glyphs, so state never depends on colour alone. */
export const STATUS_GLYPH: Record<HealthStatus, string> = {
  ok: '●',
  degraded: '▲',
  down: '✕',
};

/** The check whose error and timing represent the service on its card. */
export function worstCheck(service: WireService) {
  return (
    service.checks.find((c) => c.status === 'down') ??
    service.checks.find((c) => c.status === 'degraded') ??
    service.checks[0] ??
    null
  );
}

export interface Dependency {
  name: string;
  status: string;
  latencyMs: number | null;
  error: string | null;
}

/** Flattens the `checks` map of every readiness payload on a service. */
export function dependencies(service: WireService): Dependency[] {
  const out: Dependency[] = [];
  for (const check of service.checks) {
    const payload: ReadinessPayload | null = check.lastPayload;
    if (!payload?.checks) continue;
    for (const [name, value] of Object.entries(payload.checks)) {
      if (!value || typeof value !== 'object') continue;
      out.push({
        name,
        status: typeof value.status === 'string' ? value.status : 'unknown',
        latencyMs: typeof value.latency_ms === 'number' ? value.latency_ms : null,
        error: value.error ? String(value.error) : null,
      });
    }
  }
  return out;
}

export interface TerminalWorker {
  id: string;
  status: HealthStatus;
  connected: boolean;
  note: string | null;
  dailyUsed: number | null;
  dailyCap: number | null;
  monthlyUsed: number | null;
  monthlyCap: number | null;
  dailyRequests: number | null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function terminalWorkers(service: WireService): TerminalWorker[] {
  const out: TerminalWorker[] = [];
  const seen = new Set<string>();

  for (const check of service.checks) {
    const payload = asRecord(check.lastPayload);
    if (!payload) continue;
    const connectivity = asRecord(payload.terminals);
    if (!connectivity) continue;
    const detail = asRecord(asRecord(payload.bloomberg)?.terminals) ?? {};

    for (const [id, connectedRaw] of Object.entries(connectivity)) {
      if (seen.has(id)) continue;
      seen.add(id);

      const d = asRecord(detail[id]) ?? {};
      const connected = connectedRaw === true;
      const breakerOpen = d.breaker_open === true;
      const breakerFor = num(d.breaker_seconds_remaining);
      const limited = d.limited === true;
      const reason = typeof d.reason === 'string' && d.reason ? d.reason : null;

      let status: HealthStatus = 'ok';
      let note: string | null = null;
      if (!connected) {
        status = 'down';
        note = 'disconnected';
      } else if (breakerOpen) {
        status = 'degraded';
        note = breakerFor ? `circuit breaker open, ${breakerFor}s left` : 'circuit breaker open';
      } else if (limited) {
        status = 'degraded';
        note = reason ?? 'rate limited';
      }

      out.push({
        id,
        status,
        connected,
        note,
        dailyUsed: num(d.daily_securities_used),
        dailyCap: num(d.daily_securities_cap),
        monthlyUsed: num(d.monthly_securities_used),
        monthlyCap: num(d.monthly_securities_cap),
        dailyRequests: num(d.daily_requests),
      });
    }
  }
  return out;
}

export function quota(used: number | null, cap: number | null): string {
  if (used == null && cap == null) return '—';
  const fmt = (n: number) => n.toLocaleString('en-US');
  if (cap == null) return fmt(used!);
  return `${used == null ? '?' : fmt(used)} / ${fmt(cap)}`;
}
